import React, { useState, useEffect } from 'react';
import { Globe, Bell, Search, LogOut, User, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { globalSearch, getNotifications, markNotificationRead } from '../../lib/api';
import { useNavigate } from 'react-router-dom';

export function TopBar({ lang, onLangChange, agentName, userName, tenantId, onLogout }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [tenantId]);

  async function loadNotifications() {
    try {
      const notifs = await getNotifications(tenantId);
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch (err) { /* silent */ }
  }

  async function handleSearch(q) {
    setSearchQuery(q);
    if (q.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const results = await globalSearch(q, tenantId);
      setSearchResults(results);
    } catch (err) {
      console.error(err);
    }
    setSearching(false);
  }

  async function handleNotifRead(id) {
    try {
      await markNotificationRead(id);
      loadNotifications();
    } catch (err) { /* silent */ }
  }

  // 2026-05-26: Ctrl+K teraz obsługiwane przez globalny CommandPalette
  // (D:/ADAM/frontend/src/components/common/CommandPalette.js).
  // Stary SearchDialog poniżej pozostaje jako fallback dla admina (search
  // kontrahentów/negocjacji/raportów), ale jest **dezaktywowany** — button
  // "Szukaj" w toolbarze otwiera nowy palette przez custom event.

  return (
    <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-medium text-muted-foreground" style={{ fontFamily: 'Space Grotesk' }}>
          {agentName || 'Adam'}
          <span className="ml-2 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400">Online</span>
          </span>
        </h2>
        {userName && (
          <span className="text-xs text-muted-foreground border-l border-border pl-3 ml-1">
            {userName}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Global Search */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.dispatchEvent(new CustomEvent('maskil:open-command-palette'))}
          className="gap-2 text-muted-foreground"
          data-testid="global-search-button"
          title={lang === 'pl' ? 'Otwórz Command Palette (Ctrl+K)' : 'Open Command Palette (Ctrl+K)'}
        >
          <Search className="w-4 h-4" />
          <span className="text-xs hidden md:inline">
            {lang === 'pl' ? 'Szukaj' : 'Search'}
          </span>
          <kbd className="hidden md:inline text-xs bg-muted px-1.5 py-0.5 rounded">{"Ctrl+K"}</kbd>
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="relative"
              data-testid="notifications-button"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {notifications.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground">
                {lang === 'pl' ? 'Brak powiadomień' : 'No notifications'}
              </div>
            ) : (
              notifications.slice(0, 5).map(n => (
                <DropdownMenuItem key={n.id} onClick={() => handleNotifRead(n.id)} className="flex-col items-start gap-0.5">
                  <span className={`text-xs font-medium ${n.read ? 'text-muted-foreground' : ''}`}>{n.title}</span>
                  <span className="text-xs text-muted-foreground truncate w-full">{n.message}</span>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Language Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="language-toggle" className="gap-1.5">
              <Globe className="w-4 h-4" />
              <span className="text-xs uppercase font-medium">{lang}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onLangChange('pl')} className={lang === 'pl' ? 'bg-primary/10 text-primary' : ''}>
              Polski
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLangChange('en')} className={lang === 'en' ? 'bg-primary/10 text-primary' : ''}>
              English
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLangChange('de')} className={lang === 'de' ? 'bg-primary/10 text-primary' : ''}>
              Deutsch
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLangChange('fr')} className={lang === 'fr' ? 'bg-primary/10 text-primary' : ''}>
              Français
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLangChange('es')} className={lang === 'es' ? 'bg-primary/10 text-primary' : ''}>
              Español
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu — avatar + dropdown z Ustawieniami i Wyloguj */}
        <UserMenu userName={userName} onLogout={onLogout} lang={lang} />
      </div>

      {/* Search Dialog */}
      <SearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        query={searchQuery}
        onSearch={handleSearch}
        results={searchResults}
        searching={searching}
        lang={lang}
      />
    </header>
  );
}

function UserMenu({ userName, onLogout, lang }) {
  const navigate = useNavigate();
  const initial = (userName || 'A').trim().charAt(0).toUpperCase() || 'A';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 pl-1.5 pr-2"
          data-testid="user-menu-trigger"
          aria-label="User menu"
        >
          <span
            className="w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-semibold flex items-center justify-center"
            aria-hidden="true"
          >
            {initial}
          </span>
          <User className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {userName && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground border-b border-border">
            {userName}
          </div>
        )}
        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-menu-settings">
          <SettingsIcon className="w-3.5 h-3.5 mr-2" />
          {lang === 'pl' ? 'Ustawienia' : 'Settings'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => { if (onLogout) onLogout(); }}
          data-testid="user-menu-logout"
          className="text-red-400 focus:text-red-400"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          {lang === 'pl' ? 'Wyloguj' : 'Logout'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchDialog({ open, onOpenChange, query, onSearch, results, searching, lang }) {
  const navigate = useNavigate();

  function handleNavigate(path) {
    navigate(path);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            {lang === 'pl' ? 'Wyszukiwanie globalne' : 'Global Search'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={query}
            onChange={e => onSearch(e.target.value)}
            placeholder={lang === 'pl' ? 'Szukaj kontrahentów, negocjacji, raportów...' : 'Search contractors, negotiations, reports...'}
            autoFocus
            data-testid="global-search-input"
          />

          {searching && <div className="text-xs text-muted-foreground text-center py-2">{lang === 'pl' ? 'Szukam...' : 'Searching...'}</div>}

          {results && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {results.contractors?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1">{lang === 'pl' ? 'Kontrahenci' : 'Contractors'}</h4>
                  {results.contractors.map(c => (
                    <div key={c.id} className="p-2 rounded-lg hover:bg-muted/50 cursor-pointer text-sm" onClick={() => handleNavigate(`/contractors/${c.id}`)}>
                      {c.name} {c.company ? `(${c.company})` : ''}
                    </div>
                  ))}
                </div>
              )}
              {results.negotiations?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1">{lang === 'pl' ? 'Negocjacje' : 'Negotiations'}</h4>
                  {results.negotiations.map(n => (
                    <div key={n.id} className="p-2 rounded-lg hover:bg-muted/50 cursor-pointer text-sm" onClick={() => handleNavigate(`/negotiations/${n.id}`)}>
                      {n.title} <span className="text-muted-foreground">- {n.contractor_name}</span>
                    </div>
                  ))}
                </div>
              )}
              {results.reports?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-1">{lang === 'pl' ? 'Raporty' : 'Reports'}</h4>
                  {results.reports.map(r => (
                    <div key={r.id} className="p-2 rounded-lg hover:bg-muted/50 cursor-pointer text-sm" onClick={() => handleNavigate('/reports')}>
                      {r.summary?.slice(0, 100)}...
                    </div>
                  ))}
                </div>
              )}
              {results.total === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {lang === 'pl' ? 'Brak wyników' : 'No results'}
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
